import { useAtomValue } from 'jotai';
import { getDefaultStore } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { en, type MessageKey } from './en';
import { hi } from './hi';

// The app's own text in English and Hindi (docs/indian-touches.md, D146).
// Messages people write are never translated: that would mean reading them.
//
// Catalogs are split by area (en/chat.ts, hi/chat.ts, …) so screens can be
// translated independently; every Hindi catalog must have exactly the English
// keys (the types and i18n.test.ts check).

export type Language = 'en' | 'hi';
export type LanguageSetting = Language | 'device';

export const LANGUAGES: Array<{ code: Language; name: string }> = [
  { code: 'en', name: 'English' },
  { code: 'hi', name: 'हिन्दी' },
];

/** Per device, like sounds: this phone in Hindi, that laptop in English. */
export const languageSettingAtom = atomWithStorage<LanguageSetting>(
  'twinden:language',
  'device',
  undefined,
  { getOnInit: true }
);

/** The language a setting resolves to on this device. */
export function resolveLanguage(
  setting: LanguageSetting,
  deviceLanguages: readonly string[] = typeof navigator === 'undefined'
    ? []
    : (navigator.languages ?? [navigator.language])
): Language {
  if (setting !== 'device') return setting;
  return deviceLanguages.some((tag) => tag.toLowerCase().startsWith('hi'))
    ? 'hi'
    : 'en';
}

export function currentLanguage(): Language {
  return resolveLanguage(getDefaultStore().get(languageSettingAtom));
}

const catalogs: Record<Language, Record<MessageKey, string>> = { en, hi };

export type Vars = Record<string, string | number>;

/** Fills {name}-style holes. */
export function interpolate(text: string, vars?: Vars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (hole, name: string) =>
    name in vars ? String(vars[name]) : hole
  );
}

/** Translates for the current language; English if a string is missing. */
export function translate(
  language: Language,
  key: MessageKey,
  vars?: Vars
): string {
  return interpolate(catalogs[language][key] ?? en[key] ?? key, vars);
}

/** For code outside components (toasts from lib/, notifications). */
export function t(key: MessageKey, vars?: Vars): string {
  return translate(currentLanguage(), key, vars);
}

/**
 * For components: re-renders when the language changes.
 * `const t = useT(); t('chat.composer.everyone')`
 */
export function useT(): (key: MessageKey, vars?: Vars) => string {
  const language = resolveLanguage(useAtomValue(languageSettingAtom));
  return (key, vars) => translate(language, key, vars);
}

/** The current language, for components that pick per-language content. */
export function useLanguage(): Language {
  return resolveLanguage(useAtomValue(languageSettingAtom));
}

export type { MessageKey };
